import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import { setFixedTimeAndConvertToUtc } from 'utils/AppUtil';
import { buildCalendarWeeks } from 'utils/calendarGrid';
import { Input } from '@mui/material';

const StyledDateRangePickerWrapper = ({ className, children }) => (
    <div className={className} style={{ borderRadius: '4px' }}>
        {children}
    </div>
);

const Calendar = ({ currentMonth, selectedStart, selectedEnd, onDateClick, isStartSelection }) => {
    const calendar = useMemo(() => buildCalendarWeeks(currentMonth), [currentMonth]);

    const isInRange = useCallback(
        (date) => {
            if (!selectedStart || !selectedEnd) return false;
            return date.isBetween(selectedStart, selectedEnd, 'day', '[]');
        },
        [selectedStart, selectedEnd]
    );

    const isSelected = useCallback(
        (date) => {
            return (
                (selectedStart && date.isSame(selectedStart, 'day')) || (selectedEnd && date.isSame(selectedEnd, 'day'))
            );
        },
        [selectedStart, selectedEnd]
    );

    // function isDateDisabled(date) {
    //     if (date.isAfter(maxSelectableDate, 'day')) return true;
    //     if (!isStartSelection && selectedStart) {
    //         if (date.isBefore(selectedStart, 'day')) return false;
    //         if (Math.abs(date.diff(selectedStart, 'days')) > (maxRangeDays - 1)) return true;
    //     }
    //     if (isStartSelection && selectedEnd) {
    //         if (date.isAfter(selectedEnd, 'day')) return false;
    //         if (Math.abs(date.diff(selectedEnd, 'days')) > (maxRangeDays - 1)) return true;
    //     }
    //     return false;
    // }

    function isDateDisabled(date) {
        // Only future dates (today and later) are disabled
        return date.isAfter(moment().subtract(1, 'day'), 'day');
    }

    return (
        <div style={{ padding: '8px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                    <div key={day} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 'bold', padding: '6px 4px' }}>
                        {day}
                    </div>
                ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                {calendar.flat().map((date, index) => {
                    const inRange = isInRange(date);
                    const selected = isSelected(date);
                    const disabled = isDateDisabled(date);

                    return (
                        <button
                            key={index}
                            onClick={() => !disabled && onDateClick(date)}
                            disabled={disabled}
                            style={{
                                padding: '6px 4px',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                backgroundColor: selected ? '#0a2240' : inRange ? '#e3f2fd' : 'transparent',
                                color: selected ? 'white' : disabled ? '#bbb' : '#000',
                                fontSize: '13px',
                                opacity: disabled ? 0.55 : 1,
                                transition: 'all 0.2s',
                                minHeight: '32px',
                            }}
                            onMouseEnter={(e) => {
                                if (!selected && !inRange && !disabled) {
                                    e.target.style.backgroundColor = '#f5f5f5';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!selected && !inRange && !disabled) {
                                    e.target.style.backgroundColor = 'transparent';
                                }
                            }}
                        >
                            {date.date()}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const DateRangeInput = ({ startDate, endDate, onApply, isActive, backgroundColor }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempStart, setTempStart] = useState(startDate);
    const [tempEnd, setTempEnd] = useState(endDate);
    const [currentMonth, setCurrentMonth] = useState(moment());
    const [isSelectingStart, setIsSelectingStart] = useState(true);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        // Sync internal state when props change externally (e.g., "Today" button click)
        setTempStart(startDate);
        setTempEnd(endDate);
        setIsSelectingStart(true);
    }, [startDate, endDate]);

    const handleDateClick = useCallback(
        (date) => {
            if (isSelectingStart) {
                setTempStart(date);
                setTempEnd(null);
                setIsSelectingStart(false);
            } else {
                if (date.isBefore(tempStart)) {
                    setTempEnd(tempStart);
                    setTempStart(date);
                } else {
                    setTempEnd(date);
                }
                setIsSelectingStart(true);
            }
        },
        [isSelectingStart, tempStart]
    );

    // const handleApply = useCallback(() => {
    //     if (tempStart && tempEnd && onApply) {
    //         // Validate range <= 31 days and end <= yesterday
    //         if (Math.abs(tempEnd.diff(tempStart, 'days')) > maxRangeDays - 1) return;
    //         if (tempEnd.isAfter(maxSelectableDate, 'day')) return;
    //         onApply(tempStart, tempEnd);
    //         setIsOpen(false);
    //     }
    // }, [tempStart, tempEnd, onApply]);

    const handleApply = useCallback(() => {
        if (tempStart && tempEnd && onApply) {
            // Only block if end date is in the future
            if (tempEnd.isAfter(moment().subtract(1, 'day'), 'day')) return;

            onApply(tempStart, tempEnd);
            setIsOpen(false);
        }
    }, [tempStart, tempEnd, onApply]);

    const handleCancel = useCallback(() => {
        setTempStart(startDate);
        setTempEnd(endDate);
        setIsSelectingStart(true);
        setIsOpen(false);
    }, [startDate, endDate]);

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <Input
                type="text"
                readOnly
                onClick={() => setIsOpen(!isOpen)}
                disableUnderline
                value={
                    isActive && tempStart && tempEnd
                        ? `${tempStart.format('MM/DD/YYYY')} - ${tempEnd.format('MM/DD/YYYY')}`
                        : 'Date range'
                }
                sx={{
                    height: 40,
                    backgroundColor: isActive ? '#0a2240' : 'white',
                    border: '1px solid #0000001F',
                    borderRadius: '0 4px 4px 0',
                    padding: '0 14px',
                    paddingRight: '40px',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    color: isActive ? 'white' : '#005AA6',
                    fontWeight: 600,
                    textAlign: 'right',
                    textAlignLast: 'end',

                    // Responsive sizing: fluid width, with sensible min/max
                    width: '100%',
                    // minWidth: { xs: 110, sm: 240, md: 250, lg: 230 },
                    maxWidth: { xs: 198, sm: 350, md: 400, lg: 420 },

                    // Responsive font size based on breakpoints
                    fontSize: { xs: 12, sm: 13, md: 14 },

                    '&:before': { display: 'none' },
                    '&:after': { display: 'none' },
                }}
            />

            <svg
                style={{
                    position: 'absolute',
                    right: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '18px',
                    height: '18px',
                    color: isActive ? 'white' : '#005AA6',
                    pointerEvents: 'none',
                }}
                focusable="false"
                aria-hidden="true"
                viewBox="0 0 24 24"
            >
                <path fill="currentColor" d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"></path>
            </svg>

            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        right: 0,
                        left: 'auto',
                        backgroundColor: 'white',
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        borderRadius: '4px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        width: '280px',
                        maxWidth: 'calc(100vw - 32px)',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            borderBottom: '1px solid #eee',
                        }}
                    >
                        <button
                            onClick={() => setCurrentMonth(currentMonth.clone().subtract(1, 'month'))}
                            style={{
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                fontSize: '20px',
                                padding: '4px 8px',
                                lineHeight: 1,
                            }}
                        >
                            ‹
                        </button>
                        <span style={{ fontWeight: 'bold', fontSize: '13px' }}>{currentMonth.format('MMMM YYYY')}</span>
                        <button
                            onClick={() => setCurrentMonth(currentMonth.clone().add(1, 'month'))}
                            style={{
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                fontSize: '20px',
                                padding: '4px 8px',
                                lineHeight: 1,
                            }}
                        >
                            ›
                        </button>
                    </div>

                    <Calendar
                        currentMonth={currentMonth}
                        selectedStart={tempStart}
                        selectedEnd={tempEnd}
                        onDateClick={handleDateClick}
                        isStartSelection={isSelectingStart}
                    />

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '8px 12px',
                            borderTop: '1px solid #eee',
                            gap: '8px',
                        }}
                    >
                        <button
                            onClick={handleCancel}
                            style={{
                                padding: '8px 12px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                fontSize: '13px',
                                flex: 1,
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleApply}
                            disabled={
                                !tempStart ||
                                !tempEnd ||
                                tempEnd.isAfter(moment().subtract(1, 'day'), 'day')
                            }
                            style={{
                                padding: '8px 12px',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor:
                                    tempStart && tempEnd && !tempEnd.isAfter(moment().subtract(1, 'day'), 'day')
                                        ? '#0a2240'
                                        : '#ccc',
                                color: 'white',
                                cursor:
                                    tempStart && tempEnd && !tempEnd.isAfter(moment().subtract(1, 'day'), 'day')
                                        ? 'pointer'
                                        : 'not-allowed',
                                fontSize: '13px',
                                flex: 1,
                            }}
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default function MuiDateRangePicker({ onChange, isActive = false, backgroundColor, dateSelectionMode, selectedVerticleTab, sx }) {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const getTimezoneByTab = (tab) => {
        if (Number(tab) === 0) return "America/New_York"; // EST
        if (Number(tab) === 1) return "America/Los_Angeles"; // PST
        return "America/New_York"; // fallback to browser
    }

    const handleApply = useCallback(
        (start, end) => {
            const tz = getTimezoneByTab(selectedVerticleTab);
            const startUtc = start ? setFixedTimeAndConvertToUtc(start, tz) : null;
            const endUtc = end ? setFixedTimeAndConvertToUtc(end, tz) : null;
            setStartDate(startUtc);
            setEndDate(endUtc);
            if (onChange) {
                // You may want to send moment objects, or .toISOString() strings:
                onChange({ start: startUtc, end: endUtc });

            }
        },
        [onChange, selectedVerticleTab]
    );

    useEffect(() => {
        if (dateSelectionMode === "today") {
            setStartDate(null);
            setEndDate(null);
            // Notify parent that dates are cleared
            if (onChange) {
                onChange({ start: null, end: null });
            }
        }
    }, [dateSelectionMode, onChange]);

    return (
        <StyledDateRangePickerWrapper>
            <DateRangeInput
                startDate={startDate}
                endDate={endDate}
                onApply={handleApply}
                isActive={isActive}
                backgroundColor={backgroundColor}
                sx={sx}
            />
        </StyledDateRangePickerWrapper>
    );
}